import * as z from 'zod';

export const storefrontSearchSchema = z.object({
  status: z.enum(['published', 'unpublished']).catch('published'),
  merchant_id: z.string().catch(''),
  restaurant_id: z.string().catch(''),
  page: z.coerce.number().int().min(1).catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20),
});
export type StorefrontSearch = z.infer<typeof storefrontSearchSchema>;
export type StorefrontEntity = 'products' | 'categories' | 'restaurants' | 'promotions';

const localizedText = z.object({
  ru: z.string().optional(),
  uz: z.string().optional(),
  en: z.string().optional(),
});
const localizedTextValue = localizedText.nullish();
export const storefrontDayScheduleSchema = z
  .object({
    day_of_week: z.string().nullish(),
    open_time: z.string().nullish(),
    close_time: z.string().nullish(),
    is_closed: z.boolean().nullish(),
    order_from: z.string().nullish(),
    order_to: z.string().nullish(),
  })
  .passthrough();
export type StorefrontDaySchedule = z.infer<typeof storefrontDayScheduleSchema>;
export const storefrontItemSchema = z
  .object({
    id: z.string(),
    sort_order: z.number().int().min(1).nullish(),
    name: localizedTextValue,
    description: localizedTextValue,
    lifecycle_status: z.string().nullish(),
    accepting_orders_now: z.boolean().nullish(),
    merchant_id: z.string().nullish(),
    restaurant_id: z.string().nullish(),
    product_category_name: z.string().nullish(),
    price: z.union([z.string(), z.number()]).nullish(),
    effective_price: z.union([z.string(), z.number()]).nullish(),
    discount_value: z.union([z.string(), z.number()]).nullish(),
    discount_type: z.string().nullish(),
    promo_type: z.string().nullish(),
    starts_at: z.string().nullish(),
    ends_at: z.string().nullish(),
    product_ids: z.array(z.string()).nullish(),
    is_active: z.boolean().nullish(),
    status: z.string().nullish(),
    address: z
      .object({
        line: localizedTextValue,
        latitude: z.number().nullish(),
        longitude: z.number().nullish(),
      })
      .passthrough()
      .nullish(),
    phone: z.string().nullish(),
    rating: z.number().nullish(),
    total_order_count: z.number().nullish(),
    pending_change_request_count: z.number().nullish(),
    free_delivery_threshold: z.union([z.string(), z.number()]).nullish(),
    min_order_amount: z.union([z.string(), z.number()]).nullish(),
    min_prep_time: z.number().nullish(),
    max_prep_time: z.number().nullish(),
    timezone: z.string().nullish(),
    /** Restaurant detail only: the per-day week, merged in by `catalogApi.detail`. */
    week_schedule: z.array(storefrontDayScheduleSchema).nullish(),
    working_hours: z
      .object({ open_time: z.string().nullish(), close_time: z.string().nullish() })
      .passthrough()
      .nullish(),
    updated_at: z.string().nullish(),
    created_at: z.string().nullish(),
    icon: z.string().nullish(),
    external_id: z.string().nullish(),
    moderation_state: z.string().nullish(),
    rejection_reason: z.string().nullish(),
    product_category_id: z.string().nullish(),
    measurement_type: z.string().nullish(),
    measure_amount: z.number().nullish(),
    cook_time_seconds: z.number().nullish(),
    is_adult: z.boolean().nullish(),
    is_always_ready: z.boolean().nullish(),
    has_marked_code: z.boolean().nullish(),
    stock_quantity: z.number().nullish(),
    buy_quantity: z.number().nullish(),
    get_quantity: z.number().nullish(),
    images: z
      .array(
        z
          .object({
            id: z.string().nullish(),
            url: z.string().nullish(),
            image_type: z.string().nullish(),
          })
          .passthrough(),
      )
      .nullish(),
  })
  .passthrough();
export type StorefrontItem = z.infer<typeof storefrontItemSchema>;

export function lifecycleState(value?: string): string {
  return (value ?? '').toLowerCase().replace(/^restaurant_lifecycle_status_/, '');
}

export function storefrontText(
  value: StorefrontItem['name'] | null,
  locale: string,
): string {
  const key = locale.split('-')[0] as 'ru' | 'uz' | 'en';
  return (
    [value?.[key], value?.uz, value?.ru, value?.en].find((text) => text?.trim()) ?? '—'
  );
}
